export class GeolocationError extends Error {
  humanized: string

  constructor(type: string, humanized?: string) {
    super(type)
    this.humanized = humanized || 'Standort konnte nicht bestimmt werden.'
  }
}

export function useGeolocation(): boolean {
  return !!navigator.geolocation
}

// Ideally, there would be no reason to ever store coordinates in sessionStorage. The idea
// was that once a user has used the "current location" feature in the search, the browser
// would (via the permission and geolocation API) allow us access to the user's location
// at least for the rest of the session.
// Unfortunately, this only acceptably works on Chrome, but neither on Firefox nor Safari,
// which both err on the side of not granting us permission, unless the user has gone out
// of their way to explicitly allow it.
// Using sessionStorage and not storing location across browser sessions seem an acceptable
// compromise, although it might not be ideal from a data protection perspective.
function getGeolocationFromSessionStorage(): GeolocationCoordinates | null {
  try {
    const coords = sessionStorage?.getItem('geolocation')
    return coords ? JSON.parse(coords) : null
  } catch {
    return null
  }
}

function storeGeolocationInSessionStorage(coords: GeolocationCoordinates): void {
  try {
    sessionStorage?.setItem('geolocation', JSON.stringify(coords))
  } catch {
    // it is okay if we are not allowed
  }
}

export async function getGeolocation({
  mayAskForPermission,
  timeout = Infinity,
}: {
  mayAskForPermission: boolean
  timeout?: number
}): Promise<GeolocationCoordinates> {
  if (!useGeolocation()) {
    throw new GeolocationError('not-supported')
  }

  if (!mayAskForPermission) {
    const permissionStatus = await navigator.permissions?.query({ name: 'geolocation' })
    if (permissionStatus?.state !== 'granted') {
      const coordsFromStorage = getGeolocationFromSessionStorage()
      if (coordsFromStorage) {
        return Promise.resolve(coordsFromStorage)
      } else {
        throw new GeolocationError('no-permission')
      }
    }
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        storeGeolocationInSessionStorage(coords)
        resolve(coords)
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(
            new GeolocationError(
              'no-permission',
              'In deinem Browser ist die automatische Standorterkennung deaktiviert.',
            ),
          )
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          reject(new GeolocationError('position-unavailable'))
        } else if (error.code === error.TIMEOUT) {
          reject(new GeolocationError('timeout'))
        } else {
          reject(new GeolocationError('unknown'))
        }
      },
      { timeout },
    )
  })
}
